// ./src/components/features/Vehicle/VehicleCardHeader/VehicleCardHeader.tsx
import React from "react";
import { Group, Stack, Text, Badge } from "@mantine/core";

import { CAR_STATUSES } from "@/config";
import {
  getCarDisplayName,
  getCarSubtitle,
  getVehicleType,
  VehicleType,
} from "@/utils/carHelpers";
import { Car } from "@/utils/api/CarService";

import { CarStatusBadge } from "@/components/features/Cars/CarStatusBadge";

import { VehicleImage, VehicleImageSize } from "../VehicleImage";

// ============================================
// Types
// ============================================

export type VehicleCardHeaderSize = "sm" | "md" | "lg";

export interface VehicleCardHeaderProps {
  /** Car object */
  car: Car;

  /** Override vehicle type */
  vehicleType?: VehicleType | null;

  /** Body type ID for determining vehicle type */
  bodyTypeId?: string | number | null;

  /** Size variant */
  size?: VehicleCardHeaderSize;

  /** Whether to show the plate number badge */
  showPlate?: boolean;

  /** Whether to show the year */
  showYear?: boolean;

  /** Whether to show the status badge for non-active cars */
  showStatus?: boolean;

  /** Custom class name */
  className?: string;

  /** Click handler */
  onClick?: () => void;
}

// ============================================
// Constants
// ============================================

const SIZE_CONFIG: Record<
  VehicleCardHeaderSize,
  {
    imageSize: VehicleImageSize;
    titleSize: string;
    subtitleSize: string;
    gap: string;
    badgeSize: "xs" | "sm" | "md";
    statusBadgeSize: "xs" | "sm" | "md";
  }
> = {
  sm: {
    imageSize: "sm",
    titleSize: "sm",
    subtitleSize: "xs",
    gap: "sm",
    badgeSize: "xs",
    statusBadgeSize: "sm",
  },
  md: {
    imageSize: "md",
    titleSize: "md",
    subtitleSize: "sm",
    gap: "md",
    badgeSize: "sm",
    statusBadgeSize: "sm",
  },
  lg: {
    imageSize: "lg",
    titleSize: "lg",
    subtitleSize: "sm",
    gap: "md",
    badgeSize: "md",
    statusBadgeSize: "md",
  },
};

// ============================================
// Component
// ============================================

/**
 * Displays vehicle header with image, name, optional plate badge,
 * and status badge for non-active cars.
 * Used at the top of vehicle cards.
 */
export const VehicleCardHeader: React.FC<VehicleCardHeaderProps> = ({
  car,
  vehicleType: vehicleTypeProp,
  bodyTypeId,
  size = "md",
  showPlate = true,
  showStatus = true,
  className,
  onClick,
}) => {
  const config = SIZE_CONFIG[size];
  const vehicleType = vehicleTypeProp ?? getVehicleType(car, bodyTypeId);
  const displayName = getCarDisplayName(car);
  const subtitle = getCarSubtitle(car);

  // Get plate number from car
  const plateNumber = (car as any).plateNumber || (car as any).plate;

  // Show status badge only for non-active statuses
  const showStatusBadge =
    showStatus &&
    car.status != null &&
    car.status !== CAR_STATUSES.ACTIVE;

  const isClickable = Boolean(onClick);

  return (
    <Group
      gap={config.gap}
      wrap="nowrap"
      className={className}
      onClick={onClick}
      style={isClickable ? { cursor: "pointer" } : undefined}
    >
      <VehicleImage
        car={car}
        vehicleType={vehicleType}
        bodyTypeId={bodyTypeId}
        size={config.imageSize}
        radius="md"
        alt={displayName}
      />

      <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
        {/* Title row with name and optional plate badge */}
        <Group gap="xs" wrap="nowrap">
          <Text
            size={config.titleSize}
            fw={600}
            truncate
            style={{ flex: 1, minWidth: 0 }}
          >
            {displayName}
          </Text>

          {showPlate && plateNumber && (
            <Badge
              variant="light"
              color="gray"
              size={config.badgeSize}
              style={{ flexShrink: 0 }}
            >
              {plateNumber}
            </Badge>
          )}
        </Group>

        {/* Subtitle (make/model/year) */}
        {subtitle && (
          <Text size={config.subtitleSize} c="dimmed" truncate lh={1.3}>
            {subtitle}
          </Text>
        )}

        {/* Status badge for non-active cars */}
        {showStatusBadge && (
          <CarStatusBadge
            value={car.status}
            showIcon
            size={config.statusBadgeSize}
            mt={2}
            style={{ alignSelf: "flex-start" }}
          />
        )}
      </Stack>
    </Group>
  );
};